import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal';
import { formatKronor } from './milestonePlan';
import type { AgreementChoice, VariationBasis } from './agreementTypeChoice';

interface CreateAgreementTypeModalProps {
  projectName: string;
  /** What an ÄTA builds on; null when no ÄTA can be created. */
  basis: VariationBasis | null;
  /** Why no new ÄTA can be started yet: an earlier one is not finished (ClickUp 869fkk2jh). */
  variationBlocked?: string;
  /** The chosen flow is being opened. */
  opening?: boolean;
  error?: string;
  onCancel: () => void;
  onNext: (choice: AgreementChoice) => void;
}

/**
 * "Vad vill du skapa?" (ClickUp 869fh4c2b): step 0 before the guided wizard,
 * shown once the project has a contract signed by both parties. "Nästa"
 * opens the wizard for a new contract or for an ÄTA on the agreement in force.
 */
export default function CreateAgreementTypeModal({
  projectName,
  basis,
  variationBlocked,
  opening = false,
  error,
  onCancel,
  onNext
}: CreateAgreementTypeModalProps) {
  const { t } = useTranslation();
  const variationAvailable = !!basis && !variationBlocked;
  const [choice, setChoice] = useState<AgreementChoice>(variationAvailable ? 'variation' : 'fixed');

  const steps: Record<AgreementChoice, string[]> = {
    fixed: [
      t('contracts.wizard.steps.assignment.title'),
      t('contracts.wizard.steps.pricing.title'),
      t('contracts.wizard.steps.review.title')
    ],
    variation: [
      t('variationWizard.assignmentTitle'),
      t('variationWizard.planTitle'),
      t('contracts.wizard.steps.review.title')
    ]
  };

  const basisContract = basis && (basis.variationNumber === null
    ? t('contracts.typeChoice.basis.contract', { number: basis.contractNumber })
    : t(basis.variationNumber > 1
      ? 'contracts.typeChoice.basis.contractWithVariations'
      : 'contracts.typeChoice.basis.contractWithVariation', {
      number: basis.contractNumber,
      variation: basis.variationNumber
    }));

  const card = (value: AgreementChoice, icon: string, body: React.ReactNode) => {
    const disabled = value === 'variation' && !variationAvailable;
    const id = `agreement-type-${value}`;
    return (
      <label
        htmlFor={id}
        className={[
          'agreement-type-card',
          `is-${value}`,
          choice === value ? 'is-selected' : '',
          disabled ? 'is-disabled' : ''
        ].filter(Boolean).join(' ')}
        data-testid={id}
      >
        <span className="agreement-type-card-head">
          <input
            id={id}
            type="radio"
            name="agreement-type"
            className="form-check-input agreement-type-radio"
            value={value}
            checked={choice === value}
            disabled={disabled || opening}
            aria-describedby={`${id}-description`}
            onChange={() => setChoice(value)}
          />
          <span className="agreement-type-icon" aria-hidden="true">
            <i className={`feather-${icon}`}></i>
          </span>
          <span>
            <span className="agreement-type-card-title">{t(`contracts.typeChoice.${value}.title`)}</span>
            <span className="agreement-type-muted d-block">{t(`contracts.typeChoice.${value}.subtitle`)}</span>
          </span>
        </span>
        <span id={`${id}-description`} className="agreement-type-muted">
          {t(`contracts.typeChoice.${value}.description`)}
        </span>
        {body}
        <ol className="agreement-type-steps" aria-label={t('contracts.typeChoice.stepsLabel')}>
          {steps[value].map((step, index) => (
            <li key={step}>
              <span className="agreement-type-step-number" aria-hidden="true">{index + 1}</span>
              {step}
            </li>
          ))}
        </ol>
      </label>
    );
  };

  return (
    <Modal
      title={(
        <>
          <span className="agreement-type-project">{projectName}</span>
          <span className="d-block">{t('contracts.typeChoice.title')}</span>
        </>
      )}
      onClose={() => { if (!opening) onCancel(); }}
      size="xl"
      fullscreenBelow="md"
      dialogClassName="contract-wizard-dialog agreement-type-dialog"
      closeAriaLabel={t('contracts.wizard.closeAria')}
      footer={(
        <>
          <button type="button" className="btn btn-link agreement-type-cancel" onClick={onCancel} disabled={opening}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn contract-wizard-btn-navy"
            onClick={() => onNext(choice)}
            disabled={opening || (choice === 'variation' && !variationAvailable)}
          >
            {opening
              ? t('contracts.typeChoice.opening')
              : t('contracts.typeChoice.next', { step: steps[choice][0] })}
            {!opening && <i className="feather-arrow-right ms-2" aria-hidden="true"></i>}
          </button>
        </>
      )}
    >
      <div className="agreement-type-body">
        <div>
          <p className="contract-wizard-kicker mb-1">{t('contracts.typeChoice.stepKicker')}</p>
          <h2 className="agreement-type-heading">{t('contracts.typeChoice.heading')}</h2>
          <p className="agreement-type-muted mb-0">{t('contracts.typeChoice.intro')}</p>
        </div>

        <fieldset className="agreement-type-grid">
          <legend className="visually-hidden">{t('contracts.typeChoice.heading')}</legend>
          {card('fixed', 'file-plus', null)}
          {card('variation', 'edit-2', basis ? (
            <span className="agreement-type-basis" data-testid="agreement-type-basis">
              <span className="agreement-type-basis-label">{t('contracts.typeChoice.basis.label')}</span>
              <span className="agreement-type-basis-contract">{basisContract}</span>
              <span className="agreement-type-basis-summary">
                {t('contracts.typeChoice.basis.total', { amount: formatKronor(basis.afterRotKronor) })}
                {' · '}
                {t('contracts.typeChoice.basis.stages', { count: basis.stageCount })}
                {', '}
                {t('contracts.typeChoice.basis.completed', { count: basis.completedCount })}
              </span>
              {variationBlocked && (
                <span className="agreement-type-unavailable mt-2" data-testid="agreement-type-blocked">
                  <i className="feather-info me-2" aria-hidden="true"></i>
                  {variationBlocked}
                </span>
              )}
            </span>
          ) : (
            <span className="agreement-type-unavailable" data-testid="agreement-type-unavailable">
              <i className="feather-info me-2" aria-hidden="true"></i>
              {t('contracts.typeChoice.variation.unavailable')}
            </span>
          ))}
        </fieldset>

        <div className="agreement-type-info">
          <i className="feather-info" aria-hidden="true"></i>
          <p className="mb-0">{t('contracts.typeChoice.versionInfo')}</p>
        </div>

        {error && (
          <div className="alert alert-danger mb-0" role="alert">{error}</div>
        )}
      </div>
    </Modal>
  );
}
